import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useState } from "react";
import { Mail, Users, UserMinus, UserPlus, Plus, Sparkles, Eye, RotateCcw, X, Search,
         Send, Settings, Beaker, Save, CheckCircle2, AlertCircle, Loader2, FlaskConical } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { TID } from "@/constants/testIds";
import { toast } from "sonner";
import { TipTapEditor } from "@/components/editor/TipTapEditor";

const MOTIVATIONS = ["Tradizionalistica", "Individualistica", "Sociale", "Estetica", "Utilitaristica"];

export default function NewsletterPage() {
    const qc = useQueryClient();
    const q = useQuery({ queryKey: ["newsletters"], queryFn: () => api.get("/newsletters").then((r) => r.data) });
    const subsQ = useQuery({ queryKey: ["subscribers"], queryFn: () => api.get("/newsletters/subscribers").then((r) => r.data) });
    const unsubsQ = useQuery({ queryKey: ["unsubscribed"], queryFn: () => api.get("/newsletters/unsubscribed").then((r) => r.data) });
    const abQ = useQuery({ queryKey: ["ab-tests"], queryFn: () => api.get("/ab-tests").then((r) => r.data) });
    const brevoQ = useQuery({ queryKey: ["brevo-settings"], queryFn: () => api.get("/settings/brevo").then((r) => r.data) });

    const [openAdd, setOpenAdd] = useState(false);
    const [openAddSub, setOpenAddSub] = useState(false);
    const [editId, setEditId] = useState(null);
    const [previewId, setPreviewId] = useState(null);
    const [abForId, setAbForId] = useState(null);
    const [openSettings, setOpenSettings] = useState(false);

    const createNL = useMutation({
        mutationFn: (b) => api.post("/newsletters", b).then((r) => r.data),
        onSuccess: (n) => {
            qc.invalidateQueries({ queryKey: ["newsletters"] });
            toast.success("Newsletter creata.");
            setOpenAdd(false);
            setEditId(n.id); // open editor immediately
        },
    });
    const addSub = useMutation({
        mutationFn: (b) => api.post("/newsletters/subscribers", b).then((r) => r.data),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ["subscribers"] });
            qc.invalidateQueries({ queryKey: ["newsletters"] });
            toast.success("Iscritto aggiunto.");
            setOpenAddSub(false);
        },
        onError: () => toast.error("Errore aggiunta"),
    });
    const toggleSub = useMutation({
        mutationFn: ({ id, subscribed }) => api.patch(`/newsletters/subscribers/${id}`, { subscribed }),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ["subscribers"] });
            qc.invalidateQueries({ queryKey: ["unsubscribed"] });
            qc.invalidateQueries({ queryKey: ["newsletters"] });
        },
    });

    const data = q.data;
    const items = data?.items || [];
    const subscribers = subsQ.data || [];
    const unsubscribed = unsubsQ.data || [];
    const abTests = abQ.data || [];
    const brevo = brevoQ.data;
    const sent = items.filter((n) => n.status === "Inviata");
    const drafts = items.filter((n) => n.status !== "Inviata");

    return (
        <div className="space-y-6 pv-enter">
            <div className="flex items-end justify-between flex-wrap gap-3">
                <div>
                    <div className="pv-eyebrow mb-2">Newsletter Engine · Brevo</div>
                    <h1 className="pv-serif text-4xl tracking-tight">Comunicazioni che convincono.</h1>
                    <p className="text-[13.5px] text-textmuted mt-2 max-w-2xl">
                        Editor WYSIWYG · 5 motivazioni psicologiche · A/B test · Invio reale via Brevo · Anteprima 1:1.
                    </p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    <BrevoStatusBadge brevo={brevo} onClick={() => setOpenSettings(true)} />
                    <button
                        onClick={() => setOpenAdd(true)}
                        data-testid={TID.newsletterAdd}
                        className="flex items-center gap-1.5 bg-primary text-primary-foreground rounded-xl px-3.5 py-2 text-[13px] font-medium hover:opacity-90 transition-opacity"
                    >
                        <Plus size={14} strokeWidth={1.5} /> Nuova newsletter
                    </button>
                </div>
            </div>

            <div className="grid md:grid-cols-4 gap-3">
                <StatCard icon={Send} label="Inviate" value={sent.length} sub={`${drafts.length} in bozza`} tone="primary" />
                <StatCard icon={Users} label="Iscritti attivi" value={data?.subscribers ?? subscribers.length} sub="in newsletter" tone="success" />
                <StatCard icon={UserMinus} label="Disiscritti" value={data?.unsubscribed ?? unsubscribed.length} sub="storicamente" tone="critical" />
                <StatCard icon={Mail} label="Aperture medie" value={`${sentAvg(sent, "open_rate")}%`} sub={`CTR ${sentAvg(sent, "ctr")}%`} tone="primary" />
            </div>

            <Tabs defaultValue="newsletters" className="w-full">
                <TabsList className="bg-transparent p-0 h-auto gap-1 border-b border-border/[var(--border-opacity)] rounded-none w-full justify-start">
                    {[
                        { k: "newsletters", label: `Newsletter (${items.length})` },
                        { k: "subscribers", label: `Iscritti (${subscribers.length})` },
                        { k: "unsubscribed", label: `Disiscritti (${unsubscribed.length})` },
                        { k: "abtests", label: `A/B Test (${abTests.length})` },
                    ].map((t) => (
                        <TabsTrigger
                            key={t.k}
                            value={t.k}
                            data-testid={`newsletter-tab-${t.k}`}
                            className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:text-primary data-[state=active]:shadow-none px-4 py-2.5 text-[13px]"
                        >{t.label}</TabsTrigger>
                    ))}
                </TabsList>

                <TabsContent value="newsletters" className="mt-6">
                    <NewslettersList
                        items={items}
                        onEdit={(id) => setEditId(id)}
                        onPreview={(id) => setPreviewId(id)}
                        onAbTest={(id) => setAbForId(id)}
                    />
                </TabsContent>
                <TabsContent value="subscribers" className="mt-6">
                    <SubscribersList
                        items={subscribers}
                        onAdd={() => setOpenAddSub(true)}
                        onUnsubscribe={(id) => toggleSub.mutate({ id, subscribed: false })}
                    />
                </TabsContent>
                <TabsContent value="unsubscribed" className="mt-6">
                    <UnsubscribedList items={unsubscribed} onReactivate={(id) => toggleSub.mutate({ id, subscribed: true })} />
                </TabsContent>
                <TabsContent value="abtests" className="mt-6">
                    <ABTestList items={abTests} />
                </TabsContent>
            </Tabs>

            {openAdd && <AddNewsletterDialog onClose={() => setOpenAdd(false)} onSave={(b) => createNL.mutate(b)} busy={createNL.isPending} />}
            {openAddSub && <AddSubscriberDialog onClose={() => setOpenAddSub(false)} onSave={(b) => addSub.mutate(b)} busy={addSub.isPending} />}
            {editId && <NewsletterEditor id={editId} brevoConfigured={brevo?.configured} onClose={() => setEditId(null)} onPreview={() => { const id = editId; setEditId(null); setPreviewId(id); }} onAbTest={() => { const id = editId; setEditId(null); setAbForId(id); }} />}
            {previewId && <NewsletterPreviewDialog id={previewId} onClose={() => setPreviewId(null)} />}
            {abForId && <ABTestDialog nlId={abForId} onClose={() => setAbForId(null)} />}
            {openSettings && <BrevoSettingsDialog onClose={() => setOpenSettings(false)} />}
        </div>
    );
}

function sentAvg(items, key) {
    const arr = items.filter((n) => n[key] > 0);
    if (!arr.length) return 0;
    return Math.round((arr.reduce((s, n) => s + n[key], 0) / arr.length) * 10) / 10;
}

function BrevoStatusBadge({ brevo, onClick }) {
    const configured = brevo?.configured && brevo?.test_ok;
    return (
        <button
            onClick={onClick}
            data-testid="brevo-settings-btn"
            className={`flex items-center gap-1.5 rounded-xl border px-3 py-2 text-[12.5px] transition-colors ${
                configured
                    ? "border-success/40 bg-success/5 text-success hover:bg-success/10"
                    : "border-border/[var(--border-opacity)] hover:bg-surface2 text-textmuted"
            }`}
        >
            <Settings size={13} strokeWidth={1.5} />
            Brevo · {configured ? "Attivo" : "Configura"}
            {configured && <CheckCircle2 size={12} strokeWidth={1.5} />}
        </button>
    );
}

function StatCard({ icon: Icon, label, value, sub, tone }) {
    const toneCls = tone === "success" ? "bg-success/10 text-success" :
                    tone === "critical" ? "bg-critical/10 text-critical" :
                    "bg-primary/[0.08] text-primary";
    return (
        <div className="pv-card p-5 pv-hover">
            <div className="flex items-center gap-3">
                <div className={`h-10 w-10 rounded-xl flex items-center justify-center ${toneCls}`}>
                    <Icon size={16} strokeWidth={1.5} />
                </div>
                <div>
                    <div className="text-[11px] uppercase tracking-wider text-textsubtle">{label}</div>
                    <div className="text-2xl font-medium pv-serif">{value}</div>
                    <div className="text-[11px] text-textsubtle">{sub}</div>
                </div>
            </div>
        </div>
    );
}

function NewslettersList({ items, onPreview, onEdit, onAbTest }) {
    if (!items.length) return <div className="pv-card p-8 text-center text-textsubtle text-[13px]">Nessuna newsletter creata.</div>;
    return (
        <div className="space-y-2">
            {items.map((n) => (
                <div key={n.id} className="pv-card p-5 pv-hover">
                    <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap mb-1">
                                <MotivationBadge m={n.motivation} />
                                <span className="text-[11px] px-2 py-0.5 rounded-md bg-textsubtle/10 text-textmuted">{n.segment}</span>
                                <span className={`text-[11px] px-2 py-0.5 rounded-md ${
                                    n.status === "Inviata" ? "bg-success/10 text-success" :
                                    n.status === "Bozza" ? "bg-warning/10 text-warning" : "bg-primary/10 text-primary"
                                }`}>{n.status}</span>
                            </div>
                            <div className="text-[15px] font-medium leading-snug pv-serif">{n.subject}</div>
                            <div className="text-[12.5px] text-textmuted mt-1">{n.preheader}</div>
                        </div>
                        <div className="grid grid-cols-4 gap-6 text-right">
                            <Metric label="Destinatari" v={n.recipients} />
                            <Metric label="Aperture" v={`${n.open_rate}%`} />
                            <Metric label="CTR" v={`${n.ctr}%`} />
                            <Metric label="Conversioni" v={n.conversions} />
                        </div>
                    </div>
                    <div className="mt-3 pt-3 border-t border-border/[var(--border-opacity)] flex items-center justify-end gap-1">
                        <button
                            onClick={() => onAbTest(n.id)}
                            data-testid={`newsletter-abtest-${n.id}`}
                            className="flex items-center gap-1.5 text-[12px] px-3 py-1.5 rounded-lg hover:bg-opportunity/[0.06] text-opportunity transition-colors"
                        >
                            <FlaskConical size={13} strokeWidth={1.5} /> A/B Test
                        </button>
                        <button
                            onClick={() => onEdit(n.id)}
                            data-testid={`newsletter-edit-${n.id}`}
                            className="flex items-center gap-1.5 text-[12px] px-3 py-1.5 rounded-lg hover:bg-surface2 text-textmuted transition-colors"
                        >
                            Modifica
                        </button>
                        <button
                            onClick={() => onPreview(n.id)}
                            data-testid={`newsletter-preview-${n.id}`}
                            className="flex items-center gap-1.5 text-[12px] px-3 py-1.5 rounded-lg hover:bg-primary/[0.06] text-primary transition-colors"
                        >
                            <Eye size={13} strokeWidth={1.5} /> Anteprima
                        </button>
                    </div>
                </div>
            ))}
        </div>
    );
}

function SubscribersList({ items, onAdd, onUnsubscribe }) {
    const [q, setQ] = useState("");
    const filtered = items.filter((s) =>
        !q || `${s.first_name} ${s.last_name} ${s.email} ${s.studio}`.toLowerCase().includes(q.toLowerCase())
    );
    return (
        <div>
            <div className="flex items-center gap-2 mb-4 flex-wrap">
                <div className="flex items-center gap-2 rounded-xl border border-border/[var(--border-opacity)] bg-surface px-3 py-2 flex-1 min-w-[220px] max-w-md">
                    <Search size={14} strokeWidth={1.5} className="text-textmuted" />
                    <input
                        data-testid="newsletter-subscribers-search"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="Cerca iscritti..."
                        className="flex-1 bg-transparent outline-none text-[13px] placeholder:text-textsubtle"
                    />
                </div>
                <button
                    onClick={onAdd}
                    data-testid="newsletter-add-subscriber-btn"
                    className="flex items-center gap-1.5 bg-primary text-primary-foreground rounded-xl px-3.5 py-2 text-[13px] font-medium hover:opacity-90 transition-opacity"
                >
                    <UserPlus size={14} strokeWidth={1.5} /> Aggiungi iscritto
                </button>
            </div>

            <div className="pv-card overflow-hidden">
                <div className="grid grid-cols-12 gap-3 px-5 py-3 border-b border-border/[var(--border-opacity)] text-[11px] uppercase tracking-[0.14em] text-textsubtle">
                    <div className="col-span-4">Nome</div>
                    <div className="col-span-4">Email</div>
                    <div className="col-span-2">Studio</div>
                    <div className="col-span-1">Stato</div>
                    <div className="col-span-1 text-right">Azioni</div>
                </div>
                <div className="divide-y divide-border/[var(--border-opacity)]">
                    {filtered.map((s) => (
                        <div key={s.id} data-testid={`newsletter-subscriber-${s.id}`} className="grid grid-cols-12 gap-3 px-5 py-3 items-center text-[13px]">
                            <div className="col-span-4 font-medium truncate">{s.first_name} {s.last_name}</div>
                            <div className="col-span-4 text-textmuted truncate">{s.email}</div>
                            <div className="col-span-2 text-textmuted truncate text-[12px]">{s.studio}</div>
                            <div className="col-span-1 text-[11px] text-textsubtle">{s.state}</div>
                            <div className="col-span-1 flex justify-end">
                                <button
                                    onClick={() => onUnsubscribe(s.id)}
                                    className="text-[11.5px] text-textmuted hover:text-critical transition-colors"
                                >Disiscrivi</button>
                            </div>
                        </div>
                    ))}
                    {filtered.length === 0 && (
                        <div className="px-5 py-10 text-center text-textsubtle text-[13px]">Nessun iscritto.</div>
                    )}
                </div>
            </div>
        </div>
    );
}

function UnsubscribedList({ items, onReactivate }) {
    return (
        <div className="pv-card overflow-hidden">
            <div className="grid grid-cols-12 gap-3 px-5 py-3 border-b border-border/[var(--border-opacity)] text-[11px] uppercase tracking-[0.14em] text-textsubtle">
                <div className="col-span-4">Nome</div>
                <div className="col-span-4">Email</div>
                <div className="col-span-2">Studio</div>
                <div className="col-span-1">Aggiornato</div>
                <div className="col-span-1 text-right">Azione</div>
            </div>
            <div className="divide-y divide-border/[var(--border-opacity)]">
                {items.map((s) => (
                    <div key={s.id} className="grid grid-cols-12 gap-3 px-5 py-3 items-center text-[13px]">
                        <div className="col-span-4 font-medium truncate">{s.first_name} {s.last_name}</div>
                        <div className="col-span-4 text-textmuted truncate">{s.email}</div>
                        <div className="col-span-2 text-textmuted truncate text-[12px]">{s.studio}</div>
                        <div className="col-span-1 text-[11px] text-textsubtle">
                            {s.updated_at ? new Date(s.updated_at).toLocaleDateString("it-IT") : "—"}
                        </div>
                        <div className="col-span-1 flex justify-end">
                            <button
                                onClick={() => onReactivate(s.id)}
                                className="flex items-center gap-1 text-[11.5px] text-primary hover:underline"
                            >
                                <RotateCcw size={11} strokeWidth={1.5} /> Riattiva
                            </button>
                        </div>
                    </div>
                ))}
                {items.length === 0 && (
                    <div className="px-5 py-10 text-center text-textsubtle text-[13px]">Nessun disiscritto.</div>
                )}
            </div>
        </div>
    );
}

function ABTestList({ items }) {
    if (!items.length) return <div className="pv-card p-8 text-center text-textsubtle text-[13px]">Nessun A/B test ancora eseguito.</div>;
    return (
        <div className="space-y-3">
            {items.map((t) => (
                <div key={t.id} className="pv-card p-5 pv-hover">
                    <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-[10.5px] px-2 py-0.5 rounded-md uppercase tracking-wider font-medium bg-opportunity/10 text-opportunity">A/B Test</span>
                            <span className="text-[11px] px-2 py-0.5 rounded-md bg-textsubtle/10 text-textmuted">{t.status}</span>
                            <span className="text-[11px] px-2 py-0.5 rounded-md bg-primary/[0.08] text-primary">Winner: {t.winner} · +{t.delta_pct}%</span>
                        </div>
                        <span className="text-[11px] text-textsubtle">{new Date(t.created_at).toLocaleDateString("it-IT")}</span>
                    </div>
                    <div className="text-[13.5px] font-medium mb-3 pv-serif">{t.newsletter_subject}</div>
                    <div className="grid md:grid-cols-2 gap-3">
                        {["variant_a", "variant_b"].map((v, i) => {
                            const variant = t[v];
                            const label = v === "variant_a" ? "A" : "B";
                            const isWinner = t.winner === label;
                            return (
                                <div key={v} className={`p-3 rounded-lg border ${isWinner ? "border-primary/40 bg-primary/[0.04]" : "border-border/[var(--border-opacity)]"}`}>
                                    <div className="flex items-center justify-between mb-1">
                                        <div className="text-[10px] uppercase tracking-wider text-textsubtle">Variante {label}</div>
                                        {isWinner && <CheckCircle2 size={13} strokeWidth={1.5} className="text-primary" />}
                                    </div>
                                    <div className="text-[13px] font-medium mb-2">{variant.subject}</div>
                                    <div className="flex items-center gap-3 text-[11px] text-textmuted">
                                        <span>Apertura <b className="text-textmain">{variant.open_rate}%</b></span>
                                        <span>CTR <b className="text-textmain">{variant.ctr}%</b></span>
                                        <span>Conv. <b className="text-textmain">{variant.conversions}</b></span>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                    <div className="mt-3 pt-3 border-t border-border/[var(--border-opacity)] text-[11.5px] text-textsubtle">
                        Metrica di vittoria: <b className="text-textmain">{t.winner_metric}</b> · Split {t.split_pct}/{100 - t.split_pct}
                    </div>
                </div>
            ))}
        </div>
    );
}

function Metric({ label, v }) {
    return (
        <div>
            <div className="text-[10px] uppercase tracking-wider text-textsubtle">{label}</div>
            <div className="text-[14px] pv-mono">{v}</div>
        </div>
    );
}

function MotivationBadge({ m }) {
    const map = {
        "Tradizionalistica": "bg-primary/[0.08] text-primary",
        "Individualistica": "bg-opportunity/[0.10] text-opportunity",
        "Sociale": "bg-emerald-500/10 text-emerald-600",
        "Estetica": "bg-pink-500/10 text-pink-500",
        "Utilitaristica": "bg-warning/10 text-warning",
    };
    return <span className={`text-[11px] px-2 py-0.5 rounded-md flex items-center gap-1 ${map[m] || "bg-textsubtle/10"}`}><Sparkles size={10} strokeWidth={1.5} />{m}</span>;
}

function AddNewsletterDialog({ onClose, onSave, busy }) {
    const [form, setForm] = useState({ subject: "", preheader: "", motivation: "Utilitaristica", segment: "Tutti", status: "Bozza" });
    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target?.value ?? e }));
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-6">
            <div className="absolute inset-0 bg-navy/40 backdrop-blur-sm" onClick={onClose} />
            <div className="relative w-full max-w-lg pv-card p-6 pv-enter">
                <div className="pv-eyebrow mb-2">Nuova newsletter</div>
                <h3 className="pv-serif text-2xl mb-5">Progetta la prossima comunicazione</h3>
                <div className="space-y-3">
                    <F label="Oggetto" v={form.subject} on={set("subject")} />
                    <F label="Preheader" v={form.preheader} on={set("preheader")} />
                    <div className="grid grid-cols-2 gap-2">
                        <div>
                            <div className="pv-eyebrow mb-1">Motivazione</div>
                            <select value={form.motivation} onChange={set("motivation")} className="w-full rounded-lg border border-border/[var(--border-opacity)] bg-surface px-3 py-2 text-[13px]">
                                {MOTIVATIONS.map((m) => <option key={m} value={m}>{m}</option>)}
                            </select>
                        </div>
                        <F label="Segmento" v={form.segment} on={set("segment")} />
                    </div>
                </div>
                <div className="mt-6 flex justify-end gap-2">
                    <button onClick={onClose} className="px-3 py-2 text-[13px] rounded-lg text-textmuted hover:bg-surface2">Annulla</button>
                    <button
                        onClick={() => onSave(form)}
                        disabled={busy || !form.subject}
                        data-testid="newsletter-create-confirm"
                        className="px-3.5 py-2 text-[13px] rounded-lg bg-primary text-primary-foreground disabled:opacity-50"
                    >
                        {busy ? "Creazione..." : "Salva e apri editor"}
                    </button>
                </div>
            </div>
        </div>
    );
}

function AddSubscriberDialog({ onClose, onSave, busy }) {
    const [form, setForm] = useState({ first_name: "", last_name: "", email: "", studio: "" });
    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-6">
            <div className="absolute inset-0 bg-navy/40 backdrop-blur-sm" onClick={onClose} />
            <div className="relative w-full max-w-md pv-card p-6 pv-enter">
                <div className="pv-eyebrow mb-2">Aggiungi iscritto</div>
                <h3 className="pv-serif text-2xl mb-5">Nuovo iscritto alla newsletter</h3>
                <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-2">
                        <F label="Nome" v={form.first_name} on={set("first_name")} />
                        <F label="Cognome" v={form.last_name} on={set("last_name")} />
                    </div>
                    <F label="Email" v={form.email} on={set("email")} />
                    <F label="Studio (opzionale)" v={form.studio} on={set("studio")} />
                </div>
                <div className="mt-6 flex justify-end gap-2">
                    <button onClick={onClose} className="px-3 py-2 text-[13px] text-textmuted">Annulla</button>
                    <button
                        onClick={() => onSave(form)}
                        disabled={busy || !form.first_name || !form.last_name || !form.email}
                        data-testid="newsletter-add-subscriber-confirm"
                        className="px-3.5 py-2 text-[13px] rounded-lg bg-primary text-primary-foreground disabled:opacity-50"
                    >
                        {busy ? "Aggiunta..." : "Aggiungi"}
                    </button>
                </div>
            </div>
        </div>
    );
}

function NewsletterEditor({ id, brevoConfigured, onClose, onPreview, onAbTest }) {
    const qc = useQueryClient();
    const q = useQuery({ queryKey: ["newsletter", id], queryFn: () => api.get(`/newsletters/${id}`).then((r) => r.data) });
    const [form, setForm] = useState(null);
    const [testEmail, setTestEmail] = useState("");
    const [busySend, setBusySend] = useState(false);
    const [busyTest, setBusyTest] = useState(false);

    // Initialize form from server data once
    if (!form && q.data) {
        setForm({
            subject: q.data.subject || "",
            preheader: q.data.preheader || "",
            hero_kicker: q.data.hero_kicker || "",
            hero_title: q.data.hero_title || "",
            body_html: q.data.body_html || (q.data.body ? `<p>${q.data.body}</p>` : ""),
            cta_label: q.data.cta_label || "Scopri di più",
            cta_url: q.data.cta_url || "https://primevision.it/demo",
            motivation: q.data.motivation || "Utilitaristica",
            segment: q.data.segment || "Tutti",
            status: q.data.status || "Bozza",
        });
    }

    const save = useMutation({
        mutationFn: (b) => api.patch(`/newsletters/${id}`, b).then((r) => r.data),
        onSuccess: () => { qc.invalidateQueries({ queryKey: ["newsletters"] }); qc.invalidateQueries({ queryKey: ["newsletter", id] }); toast.success("Newsletter salvata."); },
    });

    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const doTest = async () => {
        if (!testEmail) { toast.error("Inserisci un'email di test"); return; }
        if (!brevoConfigured) { toast.error("Configura Brevo prima di inviare"); return; }
        setBusyTest(true);
        try {
            await save.mutateAsync(form);
            await api.post(`/newsletters/${id}/test-send`, { email: testEmail });
            toast.success(`Email di test inviata a ${testEmail}`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Errore invio test");
        } finally {
            setBusyTest(false);
        }
    };

    const doSend = async () => {
        if (!brevoConfigured) { toast.error("Configura Brevo prima di inviare"); return; }
        if (!window.confirm("Inviare davvero la newsletter a tutti gli iscritti?")) return;
        setBusySend(true);
        try {
            await save.mutateAsync(form);
            const { data } = await api.post(`/newsletters/${id}/send`);
            toast.success(`Newsletter inviata a ${data.recipients} iscritti!`);
            onClose();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Errore invio");
        } finally {
            setBusySend(false);
        }
    };

    if (!form) return null;

    return (
        <div className="fixed inset-0 z-50 flex justify-end">
            <div className="absolute inset-0 bg-navy/40 backdrop-blur-sm" onClick={onClose} />
            <div className="relative w-full sm:w-[720px] h-full flex flex-col pv-glass border-l border-border/[var(--border-opacity)] shadow-2xl pv-enter">
                <div className="flex items-center justify-between px-6 py-4 border-b border-border/[var(--border-opacity)]">
                    <div>
                        <div className="pv-eyebrow">Editor newsletter</div>
                        <div className="text-[15px] font-medium">{form.subject || "Nuova newsletter"}</div>
                    </div>
                    <button onClick={onClose} className="h-7 w-7 rounded-lg hover:bg-surface2 flex items-center justify-center text-textmuted">
                        <X size={15} strokeWidth={1.5} />
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto pv-scroll px-6 py-5 space-y-4">
                    <F label="Oggetto (subject line)" v={form.subject} on={(e) => set("subject", e.target.value)} />
                    <F label="Preheader (anteprima inbox)" v={form.preheader} on={(e) => set("preheader", e.target.value)} />
                    <div className="grid grid-cols-2 gap-3">
                        <F label="Hero kicker (occhiello sopra il titolo)" v={form.hero_kicker} on={(e) => set("hero_kicker", e.target.value)} />
                        <F label="Hero title (titolo grande)" v={form.hero_title} on={(e) => set("hero_title", e.target.value)} />
                    </div>

                    <div>
                        <div className="pv-eyebrow mb-1.5">Corpo della newsletter</div>
                        <TipTapEditor
                            value={form.body_html}
                            onChange={(html) => set("body_html", html)}
                            placeholder="Scrivi il messaggio principale. Usa {{first_name}} per personalizzare..."
                        />
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <F label="CTA · testo pulsante" v={form.cta_label} on={(e) => set("cta_label", e.target.value)} />
                        <F label="CTA · URL destinazione" v={form.cta_url} on={(e) => set("cta_url", e.target.value)} />
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <div className="pv-eyebrow mb-1">Motivazione psicologica</div>
                            <select value={form.motivation} onChange={(e) => set("motivation", e.target.value)} className="w-full rounded-lg border border-border/[var(--border-opacity)] bg-surface px-3 py-2 text-[13px]">
                                {MOTIVATIONS.map((m) => <option key={m} value={m}>{m}</option>)}
                            </select>
                        </div>
                        <F label="Segmento" v={form.segment} on={(e) => set("segment", e.target.value)} />
                    </div>

                    <div className="pv-card p-4 bg-primary/[0.03]">
                        <div className="pv-eyebrow mb-2">Test invio (a te)</div>
                        <div className="flex items-center gap-2">
                            <input
                                data-testid="newsletter-test-email"
                                type="email"
                                placeholder="tua-email@example.com"
                                value={testEmail}
                                onChange={(e) => setTestEmail(e.target.value)}
                                className="flex-1 rounded-lg border border-border/[var(--border-opacity)] bg-surface px-3 py-2 text-[13px] outline-none focus:border-primary"
                            />
                            <button
                                onClick={doTest}
                                disabled={busyTest || !testEmail}
                                data-testid="newsletter-test-send-btn"
                                className="flex items-center gap-1.5 text-[12.5px] px-3 py-2 rounded-lg bg-surface hover:bg-surface2 border border-border/[var(--border-opacity)] disabled:opacity-50"
                            >
                                {busyTest ? <Loader2 size={13} strokeWidth={1.5} className="animate-spin" /> : <Beaker size={13} strokeWidth={1.5} />}
                                Invia test
                            </button>
                        </div>
                        {!brevoConfigured && (
                            <div className="mt-2 flex items-center gap-1.5 text-[11px] text-warning">
                                <AlertCircle size={11} strokeWidth={1.5} /> Configura Brevo per attivare l'invio reale
                            </div>
                        )}
                    </div>
                </div>

                <div className="border-t border-border/[var(--border-opacity)] px-6 py-3 flex items-center justify-between gap-2 flex-wrap">
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => save.mutate(form)}
                            disabled={save.isPending}
                            data-testid="newsletter-save-btn"
                            className="flex items-center gap-1.5 text-[12.5px] px-3 py-2 rounded-lg hover:bg-surface2 text-textmuted disabled:opacity-50"
                        >
                            {save.isPending ? <Loader2 size={13} strokeWidth={1.5} className="animate-spin" /> : <Save size={13} strokeWidth={1.5} />}
                            Salva bozza
                        </button>
                        <button onClick={() => { save.mutate(form); onPreview(); }} className="flex items-center gap-1.5 text-[12.5px] px-3 py-2 rounded-lg hover:bg-primary/[0.06] text-primary">
                            <Eye size={13} strokeWidth={1.5} /> Anteprima
                        </button>
                        <button onClick={onAbTest} className="flex items-center gap-1.5 text-[12.5px] px-3 py-2 rounded-lg hover:bg-opportunity/[0.06] text-opportunity">
                            <FlaskConical size={13} strokeWidth={1.5} /> A/B Test
                        </button>
                    </div>
                    <button
                        onClick={doSend}
                        disabled={busySend || !brevoConfigured}
                        data-testid="newsletter-send-btn"
                        className="flex items-center gap-1.5 bg-primary text-primary-foreground rounded-xl px-4 py-2 text-[13px] font-medium disabled:opacity-50 hover:opacity-90"
                    >
                        {busySend ? <Loader2 size={13} strokeWidth={1.5} className="animate-spin" /> : <Send size={13} strokeWidth={1.5} />}
                        Invia a tutti gli iscritti
                    </button>
                </div>
            </div>
        </div>
    );
}

function NewsletterPreviewDialog({ id, onClose }) {
    const q = useQuery({ queryKey: ["nl-preview", id], queryFn: () => api.get(`/newsletters/${id}/preview`).then((r) => r.data) });
    const d = q.data;
    return (
        <div className="fixed inset-0 z-50 flex items-start justify-center p-6 overflow-y-auto pv-scroll">
            <div className="absolute inset-0 bg-navy/50 backdrop-blur-md" onClick={onClose} />
            <div className="relative w-full max-w-2xl pv-enter mt-8 mb-8">
                <div className="flex items-center justify-between mb-3 px-2">
                    <div className="text-[11px] uppercase tracking-[0.14em] text-white/80">Anteprima invio reale · così apparirà nell&apos;inbox</div>
                    <button onClick={onClose} className="h-8 w-8 rounded-lg bg-white/10 hover:bg-white/20 text-white flex items-center justify-center">
                        <X size={15} strokeWidth={1.5} />
                    </button>
                </div>
                {q.isLoading || !d ? (
                    <div className="pv-card p-10 text-center text-textmuted">Caricamento anteprima…</div>
                ) : (
                    <EmailPreview d={d} />
                )}
            </div>
        </div>
    );
}

function EmailPreview({ d }) {
    return (
        <div data-testid="newsletter-preview-panel" className="mx-auto">
            <div className="rounded-t-2xl bg-white border border-slate-200/70 p-4 flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-primary flex items-center justify-center text-white text-[13px] font-medium">PV</div>
                <div className="flex-1 min-w-0">
                    <div className="text-[13.5px] font-medium text-slate-900 truncate">PRIME VISION &lt;info@primevision.it&gt;</div>
                    <div className="text-[12px] text-slate-500 truncate">A me · {d.segment}</div>
                </div>
                <div className="text-[11px] text-slate-400">Ora</div>
            </div>

            <div className="bg-white border-x border-slate-200/70 p-10 text-slate-900">
                <div className="text-[10.5px] uppercase tracking-[0.18em] text-slate-400">Prime Vision · Newsletter</div>
                <div className="mt-1 text-[11px] text-slate-500">{d.preheader}</div>
                <div className="mt-8">
                    <div className="text-[11px] uppercase tracking-[0.14em] text-primary font-medium">{d.hero_kicker}</div>
                    <h1 className="mt-2 pv-serif text-4xl leading-tight tracking-tight text-slate-900">{d.hero_title}</h1>
                </div>
                <div className="mt-6 h-px bg-slate-100" />
                {d.body_html ? (
                    <div className="mt-8 prose prose-sm max-w-none text-slate-700 [&>p]:mb-4 [&>h2]:font-serif [&>h2]:text-2xl [&>h3]:font-medium" dangerouslySetInnerHTML={{ __html: d.body_html }} />
                ) : (
                    <p className="mt-8 text-[15px] leading-[1.7] text-slate-700">{d.body}</p>
                )}

                <div className="mt-10 flex justify-center">
                    <a href={d.cta_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 bg-primary text-white rounded-xl px-6 py-3 text-[13.5px] font-medium hover:opacity-90 transition-opacity">
                        {d.cta_label}
                    </a>
                </div>

                <div className="mt-12 h-px bg-slate-100" />
                <div className="mt-6 text-[11.5px] leading-relaxed text-slate-500">
                    Questa comunicazione è stata calibrata sul profilo motivazionale <span className="text-primary font-medium">{d.motivation}</span>.
                </div>
            </div>

            <div className="rounded-b-2xl bg-slate-50 border border-t-0 border-slate-200/70 px-10 py-6 text-[11px] text-slate-500 space-y-2">
                <div className="flex items-center gap-2.5">
                    <div className="h-5 w-5 rounded-md bg-primary flex items-center justify-center">
                        <span className="h-1.5 w-1.5 rounded-full bg-white" />
                    </div>
                    <span className="text-slate-700 font-medium">Prime Vision · Software per studi oculistici</span>
                </div>
                <div>Via degli Oculisti, 12 · Milano · Italia</div>
                <div>Ricevi questa email perché sei iscritto alla newsletter Prime Vision.</div>
                <div><a className="text-primary underline underline-offset-2" href="#">Disiscriviti</a> · <a className="text-primary underline underline-offset-2" href="#">Preferenze</a></div>
            </div>
        </div>
    );
}

function ABTestDialog({ nlId, onClose }) {
    const qc = useQueryClient();
    const q = useQuery({ queryKey: ["newsletter", nlId], queryFn: () => api.get(`/newsletters/${nlId}`).then((r) => r.data) });
    const [a, setA] = useState({ subject: "", body: "" });
    const [b, setB] = useState({ subject: "", body: "" });
    const [metric, setMetric] = useState("open_rate");
    const [split, setSplit] = useState(50);

    if (q.data && !a.subject) {
        setA({ subject: q.data.subject, body: q.data.body_html || q.data.body || "" });
        setB({ subject: q.data.subject + " — v2", body: q.data.body_html || q.data.body || "" });
    }

    const run = useMutation({
        mutationFn: () => api.post("/newsletters/ab-test", {
            newsletter_id: nlId,
            variant_a_subject: a.subject, variant_a_body: a.body,
            variant_b_subject: b.subject, variant_b_body: b.body,
            split_pct: split, winner_metric: metric,
        }).then((r) => r.data),
        onSuccess: (t) => {
            qc.invalidateQueries({ queryKey: ["ab-tests"] });
            toast.success(`Winner: variante ${t.winner} (+${t.delta_pct}%)`);
            onClose();
        },
        onError: () => toast.error("Errore A/B test"),
    });

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-6">
            <div className="absolute inset-0 bg-navy/40 backdrop-blur-sm" onClick={onClose} />
            <div className="relative w-full max-w-3xl pv-card p-6 pv-enter max-h-[86vh] overflow-y-auto pv-scroll">
                <div className="flex items-center justify-between mb-4">
                    <div>
                        <div className="pv-eyebrow mb-1">A/B Test · newsletter</div>
                        <h3 className="pv-serif text-2xl">Confronta 2 varianti</h3>
                    </div>
                    <button onClick={onClose} className="h-8 w-8 rounded-lg hover:bg-surface2 text-textmuted"><X size={15} strokeWidth={1.5} className="mx-auto" /></button>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                        <div className="pv-eyebrow">Variante A</div>
                        <F label="Oggetto" v={a.subject} on={(e) => setA({ ...a, subject: e.target.value })} />
                        <div>
                            <div className="pv-eyebrow mb-1">Corpo</div>
                            <textarea rows={4} value={a.body} onChange={(e) => setA({ ...a, body: e.target.value })}
                                      className="w-full rounded-lg border border-border/[var(--border-opacity)] bg-surface px-3 py-2 text-[12.5px] outline-none focus:border-primary" />
                        </div>
                    </div>
                    <div className="space-y-2">
                        <div className="pv-eyebrow">Variante B</div>
                        <F label="Oggetto" v={b.subject} on={(e) => setB({ ...b, subject: e.target.value })} />
                        <div>
                            <div className="pv-eyebrow mb-1">Corpo</div>
                            <textarea rows={4} value={b.body} onChange={(e) => setB({ ...b, body: e.target.value })}
                                      className="w-full rounded-lg border border-border/[var(--border-opacity)] bg-surface px-3 py-2 text-[12.5px] outline-none focus:border-primary" />
                        </div>
                    </div>
                </div>
                <div className="mt-4 grid grid-cols-2 gap-3">
                    <div>
                        <div className="pv-eyebrow mb-1">Metrica di vittoria</div>
                        <select value={metric} onChange={(e) => setMetric(e.target.value)} className="w-full rounded-lg border border-border/[var(--border-opacity)] bg-surface px-3 py-2 text-[13px]">
                            <option value="open_rate">Apertura (%)</option>
                            <option value="ctr">CTR (%)</option>
                            <option value="conversions">Conversioni</option>
                        </select>
                    </div>
                    <div>
                        <div className="pv-eyebrow mb-1">Split (%A)</div>
                        <input type="number" min="10" max="90" value={split} onChange={(e) => setSplit(Number(e.target.value))}
                               className="w-full rounded-lg border border-border/[var(--border-opacity)] bg-surface px-3 py-2 text-[13px]" />
                    </div>
                </div>
                <div className="mt-6 flex justify-end gap-2">
                    <button onClick={onClose} className="px-3 py-2 text-[13px] text-textmuted">Annulla</button>
                    <button
                        onClick={() => run.mutate()}
                        disabled={run.isPending || !a.subject || !b.subject}
                        data-testid="ab-test-run-btn"
                        className="flex items-center gap-1.5 px-3.5 py-2 text-[13px] rounded-lg bg-primary text-primary-foreground disabled:opacity-50"
                    >
                        {run.isPending ? <Loader2 size={13} strokeWidth={1.5} className="animate-spin" /> : <FlaskConical size={13} strokeWidth={1.5} />}
                        Esegui A/B test
                    </button>
                </div>
            </div>
        </div>
    );
}

function BrevoSettingsDialog({ onClose }) {
    const qc = useQueryClient();
    const q = useQuery({ queryKey: ["brevo-settings"], queryFn: () => api.get("/settings/brevo").then((r) => r.data) });
    const [form, setForm] = useState(null);
    const [testing, setTesting] = useState(false);
    const [testResult, setTestResult] = useState(null);

    if (q.data && !form) {
        setForm({
            api_key: "",
            sender_email: q.data.sender_email || "info@primevision.it",
            sender_name: q.data.sender_name || "PRIME VISION",
        });
    }

    const save = useMutation({
        mutationFn: (b) => api.patch("/settings/brevo", b).then((r) => r.data),
        onSuccess: () => { qc.invalidateQueries({ queryKey: ["brevo-settings"] }); toast.success("Impostazioni salvate."); },
    });

    const test = async () => {
        setTesting(true);
        setTestResult(null);
        try {
            const { data } = await api.post("/settings/brevo/test");
            setTestResult({ ok: true, ...data });
            qc.invalidateQueries({ queryKey: ["brevo-settings"] });
        } catch (e) {
            setTestResult({ ok: false, error: e?.response?.data?.detail || "Errore" });
        } finally {
            setTesting(false);
        }
    };

    if (!form) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-6">
            <div className="absolute inset-0 bg-navy/40 backdrop-blur-sm" onClick={onClose} />
            <div className="relative w-full max-w-md pv-card p-6 pv-enter">
                <div className="flex items-center justify-between mb-4">
                    <div>
                        <div className="pv-eyebrow mb-1">Integrazione</div>
                        <h3 className="pv-serif text-2xl">Impostazioni Brevo</h3>
                    </div>
                    <button onClick={onClose} className="h-8 w-8 rounded-lg hover:bg-surface2 text-textmuted"><X size={15} strokeWidth={1.5} className="mx-auto" /></button>
                </div>

                <div className="space-y-3">
                    <div>
                        <div className="pv-eyebrow mb-1">API Key {q.data?.api_key_masked && <span className="text-textmuted">(attuale: {q.data.api_key_masked})</span>}</div>
                        <input
                            data-testid="brevo-api-key-input"
                            type="password"
                            placeholder="xkeysib-..."
                            value={form.api_key}
                            onChange={(e) => setForm((f) => ({ ...f, api_key: e.target.value }))}
                            className="w-full rounded-lg border border-border/[var(--border-opacity)] bg-surface px-3 py-2 text-[13px] pv-mono outline-none focus:border-primary focus:pv-ring-primary"
                        />
                        <div className="mt-1 text-[10.5px] text-textsubtle">Ottieni la key da https://app.brevo.com/settings/keys/api</div>
                    </div>
                    <F label="Email mittente (verificata su Brevo)" v={form.sender_email} on={(e) => setForm((f) => ({ ...f, sender_email: e.target.value }))} />
                    <F label="Nome mittente" v={form.sender_name} on={(e) => setForm((f) => ({ ...f, sender_name: e.target.value }))} />
                </div>

                {testResult && (
                    <div className={`mt-4 p-3 rounded-lg border flex items-start gap-2 ${testResult.ok ? "border-success/40 bg-success/[0.05] text-success" : "border-critical/40 bg-critical/[0.05] text-critical"}`}>
                        {testResult.ok ? <CheckCircle2 size={14} strokeWidth={1.5} className="mt-0.5" /> : <AlertCircle size={14} strokeWidth={1.5} className="mt-0.5" />}
                        <div className="text-[12px]">
                            {testResult.ok
                                ? <>Connessione OK. Account: <b>{testResult.account?.email || testResult.account?.companyName}</b></>
                                : <>Errore: {testResult.error}</>}
                        </div>
                    </div>
                )}

                <div className="mt-6 flex justify-between gap-2">
                    <button
                        onClick={test}
                        disabled={testing}
                        data-testid="brevo-test-btn"
                        className="flex items-center gap-1.5 text-[12.5px] px-3 py-2 rounded-lg hover:bg-surface2 border border-border/[var(--border-opacity)] disabled:opacity-50"
                    >
                        {testing ? <Loader2 size={13} strokeWidth={1.5} className="animate-spin" /> : <Beaker size={13} strokeWidth={1.5} />}
                        Testa connessione
                    </button>
                    <div className="flex gap-2">
                        <button onClick={onClose} className="px-3 py-2 text-[13px] text-textmuted">Annulla</button>
                        <button
                            onClick={() => { save.mutate(form); }}
                            disabled={save.isPending}
                            data-testid="brevo-save-btn"
                            className="flex items-center gap-1.5 px-3.5 py-2 text-[13px] rounded-lg bg-primary text-primary-foreground disabled:opacity-50"
                        >
                            {save.isPending ? <Loader2 size={13} strokeWidth={1.5} className="animate-spin" /> : <Save size={13} strokeWidth={1.5} />}
                            Salva
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

function F({ label, v, on }) {
    return (
        <label className="block">
            <div className="pv-eyebrow mb-1">{label}</div>
            <input value={v} onChange={on} className="w-full rounded-lg border border-border/[var(--border-opacity)] bg-surface px-3 py-2 text-[13px] outline-none focus:border-primary focus:pv-ring-primary" />
        </label>
    );
}
